<template>
  <div>
    <div
      :class="!isMobile && 'p-3'"
      v-if="
        selectIsPermitted('winbackWriteAccess') ||
        (selectIsPermitted('noteWriteAccess') && internalNotesEnabled)
      "
    >
      <div v-if="nickname" class="pl-4 pb-1 font-weight-bold" style="color: var(--blue)">
        {{ nickname }}
        <span class="font-weight-light">is attached</span>
      </div>
      <div
        class="mb-0 message-builder-card"
        :class="{
          'p-2 pb-4 bg-white border-top': isMobile,
          'card p-3': !isMobile,
          'note-card': isNote
        }"
        v-if="location && location.bandwidth && isMarketingOptIn"
      >
        <textarea-autosize
          v-model="message"
          @input="typing"
          ref="messageInput"
          class="form-control message-input"
          style="border: none"
          :placeholder="'Reply here...'"
          rows="1"
          v-if="!isNote && !isGenerating"
        ></textarea-autosize>

        <div
          v-if="isGenerating"
          class="d-flex text-muted align-items-center px-3"
          style="height: 40px"
        >
          <b-spinner small variant="primary" class="mr-2" />
          Generating...
        </div>

        <QuillEditor v-model="message" :options="editorOptions" v-if="isNote" class="isNote" />

        <!-- action row -->
        <div class="d-flex justify-content-between">
          <div class="d-flex align-items-center" style="padding-left: 11px">
            <button
              class="btn btn-link btn-sm action-icon p-0 mb--1"
              style="font-size: 20px"
              :style="{ color: AaIconShade }"
              @click="setIsNote(false)"
              title="Text Message"
              v-if="selectIsPermitted('winbackWriteAccess')"
            >
              Aa
            </button>
            <span title="Note">
              <Edit3Icon
                size="1.45x"
                class="action-icon"
                :style="{ color: noteIconShade }"
                @click="setIsNote(true)"
                v-if="internalNotesEnabled && selectIsPermitted('noteWriteAccess')"
              ></Edit3Icon>
            </span>
            <TemplateDropdownV2
              class="action-icon"
              ref="templateDropdown"
              @selected="addTemplate"
              @reset="resetTemplate"
              :includeModal="true"
              :locationId="activeConversationLocationId"
              :companyId="selectActiveConversationCompanyId"
              :key="selectActiveConversationCompanyId + activeConversationLocationId"
              v-if="!isNote && canUseTemplate"
            />
            <div
              v-if="showSelectedDropdown && selectIsPermitted('promosSendAccess')"
              @click="openPromo"
            >
              <span class="d-flex align-items-center pr-1">
                <GiftIcon class="action-icon" size="1.4x" style="color: var(--blue)" />
              </span>
            </div>
            <PromoDropdownV2
              v-show="!showSelectedDropdown && selectIsPermitted('promosSendAccess')"
              class="action-icon"
              ref="promoDropdown"
              @selected="addPromo"
              @reset="resetPromo"
              :includeUniquePromos="true"
              :includeModal="true"
              source="winback"
              v-if="!isNote"
            />
            <LoyaltyDropdownV2
              class="action-icon"
              v-if="companyLoyaltyEnabled && !isNote"
              :customerId="activeConversationCustomerId"
              :companyId="selectActiveConversationCompanyId"
              :locationId="activeConversationLocationId"
              ref="loyaltyDropdown"
            />
            <button
              id="aiButton"
              class="btn btn-link p-0"
              @click="generateResponse"
              :disabled="isGenerating"
              v-if="showGenerateReply"
              title="Generate AI Response"
            >
              <inline-svg
                :src="'/img/ai-icon.svg'"
                :transformSource="(svg) => svg"
                width="23"
                height="23"
                aria-label="My image"
                class="action-icon"
              ></inline-svg>
            </button>
            <GiftCardButton
              class="action-icon"
              :key="refreshKey"
              @setAmount="setGiftCardAmount"
              v-if="giftCardEnabled && giftCardWritePermitted && !isNote"
            />
            <button
              id="createTicketButton"
              class="btn btn-link p-0"
              @click="openCreateTicket"
              title="Create ticket"
              v-if="canCreateTicket"
              :disabled="selectActiveConversationLoading"
            >
              <i class="far fa-ticket fa-xl action-icon"></i>
            </button>
          </div>

          <div>
            <button class="btn btn-link send-btn mb--1" :disabled="sending" @click="processMessage">
              <span
                :class="!message ? 'inactive-send' : isNote ? 'active-add' : 'active-send'"
                v-if="!sending"
              >
                {{ isNote ? 'Add' : 'Send' }}
              </span>
              <b-spinner small variant="primary" v-else />
            </button>
          </div>
        </div>
      </div>

      <div class="card p-3 mb-0" v-else-if="location && location._id && !location.bandwidth">
        <div class="alert alert-light fade show mb-0 text-center" role="alert">
          <strong>Uh-oh!</strong>
          SMS capabilities are not enabled for your
          {{ location.friendlyName || location.city }} location. <br />
          <a href="#" @click.prevent="handleMailto('hello@ovationup.com')">Contact us</a> to enable
          them.
        </div>
      </div>

      <div class="card p-3 mb-0" v-else-if="!isMarketingOptIn">
        <div class="alert alert-light fade show mb-0 text-center" role="alert">
          <strong>This customer has chosen not to receive messages anymore.</strong>
          <br />
          If you have any questions or need assistance, please
          <a href="#" @click.prevent="handleMailto('support@ovationup.com')">Contact us</a>. We're
          here to help!
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Config from '@/config'
import { trackEvent } from '@/lib/analytics'
import { Edit3Icon, GiftIcon } from 'vue-feather-icons'
import TemplateService from '@/services/TemplateService'
import PromoDropdownV2 from '@/components/Modules/Dropdowns/PromoDropdownV2'
import TemplateDropdownV2 from '@/components/Modules/Dropdowns/TemplateDropdownV2'
import LoyaltyDropdownV2 from '@/components/Modules/Loyalty/LoyaltyDropdownV2'
import GiftCardButton from '@/components/Modules/GiftCard/Components/GiftCardButton'
import { createNamespacedHelpers } from 'vuex'
import { isMobile } from 'mobile-device-detect'
import { sanitizeInput } from '@/lib/validation'
import { hasGiftCardWriteAccess } from '@/lib/gift-card-permissions'
import { generateIdempotencyKey } from '@/lib/gift-card-idempotency-key'
import {
  giftCardErrorMessage,
  giftCardIssuedReplyFailedMessage
} from '@/lib/gift-card-error-message'
import { debounce } from 'lodash-es'

// quill imports
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css' // import styles
import 'quill/dist/quill.snow.css' // for snow theme
import 'quill-mention/dist/quill.mention.css'
import Quill from 'quill'
import Mention from 'quill-mention'
Quill.register('modules/mentions', Mention)

const ConversationModuleV2 = createNamespacedHelpers('conversationV2')
const UserModule = createNamespacedHelpers('user')
const DataCoreModule = createNamespacedHelpers('dataCore')
const CompanyModule = createNamespacedHelpers('company')
const TimelineModuleV2 = createNamespacedHelpers('timelineV2')
const LocalStorageModule = createNamespacedHelpers('localStorage')

export default {
  name: 'MessageBuilder',
  components: {
    GiftIcon,
    PromoDropdownV2,
    TemplateDropdownV2,
    LoyaltyDropdownV2,
    QuillEditor: quillEditor,
    Edit3Icon,
    GiftCardButton
  },
  data: () => ({
    message: '',
    promoId: undefined,
    highlightResolve: false,
    sending: false,
    isMobile,
    isNote: false,
    isGenerating: false,
    giftCardAmount: null,
    // Generated once per staged amount, reused across retries of the same send so
    // the backend can dedupe on it once the issue endpoint accepts one. Cleared
    // everywhere giftCardAmount is cleared.
    giftCardIdempotencyKey: null,
    refreshKey: 0
  }),
  mounted() {
    document.addEventListener('keydown', this.keyListener)
    if (!this.selectIsPermitted('winbackWriteAccess')) {
      this.setIsNote(true)
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.keyListener)
  },
  computed: {
    ...DataCoreModule.mapGetters(['selectCompanyById', 'selectLocationById', 'selectPromoById']),
    ...UserModule.mapGetters(['onlineUser', 'selectActiveCompanyUsersByConversationLocation']),
    ...ConversationModuleV2.mapGetters([
      'selectActiveConversation',
      'selectActiveConversationCustomer',
      'selectActiveConversationLocation',
      'selectActiveConversationCompanyId',
      'selectActiveConversationLoading'
    ]),
    ...TimelineModuleV2.mapGetters(['selectLastEvent', 'selectLastActionableEvent']),
    ...CompanyModule.mapGetters(['activeCompany', 'selectIsPermitted', 'selectTicketingEnabled']),
    showGenerateReply() {
      return (
        !this.isNote &&
        this.selectIsPermitted('winbackAiResponseAccess') &&
        this.activeCompany?.settings?.winbackAiEnabled &&
        this.selectActiveConversation?.lastCommunication?.type === 'feedback'
      )
    },
    canUseTemplate() {
      return this.selectIsPermitted('winbackTemplateAccess')
    },
    canCreateTicket() {
      return (
        this.activeCompany?._id !== 'ALL_COMPANIES' &&
        this.selectTicketingEnabled &&
        this.selectIsPermitted('ticketsCreateAccess')
      )
    },
    AaIconShade() {
      return this.isNote ? '#95aac9' : '#12263F'
    },
    noteIconShade() {
      return this.isNote ? '#12263F' : '#95aac9'
    },
    activeConversationLocationId() {
      return this.selectActiveConversationLocation?._id
    },
    activeConversationCustomerId() {
      return this.selectActiveConversationCustomer?._id
    },
    lastCommunicationType() {
      return this.selectActiveConversation?.lastCommunication?.type
    },
    activeConversationCompanyId() {
      // The conversation's company arrives either as an id or populated.
      const company = this.selectActiveConversationCompanyId
      return company?._id ?? company
    },
    internalNotesEnabled() {
      // Resolved from the conversation's company, not the active one: in the
      // All Companies view the active company is a placeholder with no
      // settings, which hid the note action for every brand. Going through the
      // conversation also keeps a brand with notes disabled disabled there.
      return this.selectCompanyById(this.activeConversationCompanyId)?.settings
        ?.internalNotesEnabled
    },
    location() {
      return this.selectActiveConversationLocation
        ? this.selectLocationById(
            this.selectActiveConversationLocation._id || this.selectActiveConversationLocation
          )
        : null
    },
    nickname() {
      const promo = this.selectPromoById(this.promoId)
      return this.isMobile && (promo?.nickname || promo?.title)
    },
    companyLoyaltyEnabled() {
      return this.selectCompanyById(this.selectActiveConversationCompanyId)?.loyalty?.enabled
        ? true
        : false
    },
    giftCardEnabled() {
      return this.selectActiveConversation?.location?.settings?.giftCardEnabled ? true : false
    },
    // Fail-closed, matching the backend gate — see lib/gift-card-permissions.
    giftCardWritePermitted() {
      return hasGiftCardWriteAccess(this.onlineUser?.role, this.activeCompany?.permissions)
    },
    editorOptions() {
      let self = this
      return {
        modules: {
          mentions: {
            allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
            mentionDenotationChars: ['@'],
            source: function (searchTerm, renderList, mentionChar) {
              const values = self.selectActiveCompanyUsersByConversationLocation

              if (searchTerm?.length === 0) {
                renderList(values, searchTerm)
              } else {
                const matches = []
                for (let i = 0; i < values.length; i++)
                  if (~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase()))
                    matches.push(values[i])
                renderList(matches, searchTerm)
              }
            },
            defaultMenuOrientation: 'top',
            dataAttributes: ['fullName'],
            renderItem: function (item) {
              return `
                <div style="white-space: nowrap">
                  <img
                    src="${item.avatar}"
                    alt="Avatar"
                    class="avatar"
                    style="display: ${item.avatar ? 'inline-block' : 'none'}"
                  >
                  <span
                    class="initials-avatar"
                    style="display: ${item.avatar ? 'none' : 'inline-block'}"
                  >
                    ${item.firstName[0] + item.lastName[0]}
                  </span>
                  <span style="vertical-align: middle;">
                    ${item.value}
                  </span>
                </div>
              `
            },
            isolateCharacter: true
          },
          toolbar: false
        },
        placeholder: 'Write an internal note...'
      }
    },
    showSelectedDropdown() {
      return !!(isMobile && this.promoId)
    },
    isMarketingOptIn() {
      const { customer = {}, location = {} } = this.selectActiveConversation || {}
      const { globalUnsubscribe = false, unsubscribed = [] } = customer
      const { _id: locationId } = location
      return !globalUnsubscribe && !unsubscribed.includes(locationId)
    }
  },
  methods: {
    ...ConversationModuleV2.mapActions(['sendReply', 'issueGiftCard', 'saveNote', 'generateReply']),
    ...LocalStorageModule.mapActions([
      'getCachedMessage',
      'setMessageCache',
      'removeMessageCache',
      'getGiftCardIdempotencyKey',
      'saveGiftCardIdempotencyKey',
      'clearGiftCardIdempotencyKey'
    ]),
    ...TimelineModuleV2.mapActions(['fetchTimeline', 'setExpectingReply']),
    keyListener(e) {
      if (e.key === 'Enter' && e.metaKey) {
        e.preventDefault()
        this.processMessage()
      }
    },
    typing(e) {
      this.message = sanitizeInput(e)
    },
    openCreateTicket() {
      // The create-ticket form lives in the host app, not in this iframe.
      // The host scopes the ticket to its own active company, which can move ahead of the
      // conversation shown here during a company switch, so it ignores a mismatched one.
      const company = this.selectActiveConversationCompanyId
      window.parent.postMessage(
        {
          event: 'OPEN_CREATE_TICKET',
          conversationId: this.selectActiveConversation._id,
          companyId: company?._id ?? company
        },
        Config.baseAppUrl
      )
    },
    setIsNote(status) {
      this.isNote = status
      if (status === false) {
        this.message = ''
      }
    },
    async processMessage() {
      if (this.sending === true || !this.message) return
      this.sending = true
      if (this.isNote) {
        await this.createNote()
      } else {
        await this.sendMessage()
      }
      this.sending = false
    },
    async createNote() {
      const rawMentions = this.message.match(/data-value="[^"]*/gm) || []
      const mentions = rawMentions.reduce((acc, curr) => {
        acc.push(curr.replace(/data-value="/gm, ''))
        return acc
      }, [])
      if (mentions?.length) {
        const mentionedUsers = mentions.reduce((acc, curr) => {
          const match = this.selectActiveCompanyUsersByConversationLocation.find(
            (u) => u.value === curr
          )
          if (match) {
            acc.push(match)
          }
          return acc
        }, [])
        const mentionsRegex = new RegExp('(' + mentions.join('|') + ')', 'gm')
        const bracketedNamesMessage = this.message.replace(mentionsRegex, '{$1}')
        const formattedMessage = bracketedNamesMessage.replace(/<[^<]*>/gm, '').trim()
        await this.saveNote({
          conversationId: this.selectActiveConversation._id,
          text: formattedMessage,
          mentions: Array.from(mentionedUsers).map((uM) => ({
            userId: uM._id,
            atFallbackText: uM.value
          }))
        })
      } else {
        const formattedMessage = this.message.replace(/<[^<]*>/gm, '').trim()
        await this.saveNote({
          conversationId: this.selectActiveConversation._id,
          text: formattedMessage,
          mentions: []
        })
      }
      this.message = ''
      trackEvent(this.$intercom, 'Created an internal note', {
        userId: this.onlineUser?._id,
        companyId: this.activeCompany?._id
      })
      await this.removeMessageCache(this.selectActiveConversation._id)
    },
    handleMailto(mailtoString) {
      window.parent.postMessage({ mailto: `mailto:${mailtoString}` }, Config.baseAppUrl)
    },
    async sendMessage() {
      if (!this.message) return
      if (this.giftCardAmount) {
        const verified = await this.verifyGiftCard()
        if (!verified) return
      }

      // Issuance runs BEFORE the reply. A declined or misconfigured card must not
      // be followed by a reply that promises one, so a failure here aborts
      // without sending the message and the operator can retry.
      const sentGiftCard = !!this.giftCardAmount
      const conversationId = this.selectActiveConversation._id
      if (this.giftCardAmount) {
        // Set before the issue call, not after: a websocket gift-card event can land
        // in the gap between the issue response and sendReply's own optimistic event
        // existing, and this is what makes the timeline hold it either way.
        await this.setExpectingReply({ conversationId, value: true })
        const issued = await this.issueGiftCardForConversation()
        if (!issued) {
          await this.setExpectingReply({ conversationId, value: false })
          return
        }
        // Cleared as soon as the card exists: if the reply below fails, a retry
        // must not re-issue a second card for the same send. The persisted copy goes
        // too — left behind, the operator's next deliberate card for this guest would
        // replay this one's response and report a success that never happened.
        this.giftCardAmount = null
        this.giftCardIdempotencyKey = null
        await this.clearGiftCardIdempotencyKey(this.selectActiveConversation?.customer?._id)

        // A conversation switch in the gap between issuance and the reply below
        // would send guest B a message promising a card that was actually issued
        // to guest A. sendReply always targets the currently active conversation,
        // so bail rather than let it silently target the wrong guest.
        if (this.selectActiveConversation._id !== conversationId) {
          await this.setExpectingReply({ conversationId, value: false })
          this.$notify({
            text: 'The gift card was created, but the conversation changed before the message could send. Resend it from the original conversation.',
            type: 'warn'
          })
          return
        }
      }

      try {
        await this.sendReply({
          promoId: this.promoId,
          message: this.message
        })
        if (sentGiftCard) {
          await this.refreshTimelineForGiftCard()
        }
        trackEvent(this.$intercom, 'Sent_Reply')
        if (this.promoId) {
          trackEvent(this.$intercom, 'Sent_Promo')
        }

        this.resetAll()

        await this.removeMessageCache(this.selectActiveConversation._id)

        setTimeout(() => {
          this.$root.$emit('bv::show::tooltip', 'resolveButton')
        }, 2000)
      } catch (error) {
        const text = sentGiftCard ? giftCardIssuedReplyFailedMessage() : 'Failed to send message'
        this.$notify({ text, type: 'error' })
      } finally {
        if (sentGiftCard) {
          await this.setExpectingReply({ conversationId, value: false })
        }
      }
    },
    /**
     * Pulls the card's timeline entry in after the reply has been sent. Refetches
     * rather than synthesizing a placeholder — the issue response carries no card
     * `_id`/`last4Digits`. Fallback for the websocket event, not the primary path.
     */
    async refreshTimelineForGiftCard() {
      try {
        await this.fetchTimeline({ latest: true })
      } catch (error) {
        // Non-fatal: the card is issued and the reply is sent regardless, and the
        // entry still arrives over the websocket.
        console.error('Failed to refresh the timeline after a gift-card send', error)
      }
    },
    /**
     * Issues the card and reports whether sendMessage should continue. No re-entry
     * guard of its own — the send button stays disabled while `sending` is true,
     * and the service also rate-limits a second issue within 60s to a 429.
     */
    async issueGiftCardForConversation() {
      try {
        const result = await this.issueGiftCard({
          // The modal collects dollars ($10–$100); the endpoint takes an integer
          // number of cents and rejects anything else. Math.round, not a bare
          // multiply: `25.10 * 100` is 2509.9999999999995 in float, which fails the
          // integer check.
          amountCents: Math.round(parseFloat(this.giftCardAmount) * 100),
          // Refetching here would render the card entry before the reply that
          // introduces it. refreshTimelineForGiftCard refetches after the reply.
          refreshTimeline: false,
          idempotencyKey: this.giftCardIdempotencyKey
        })
        trackEvent(this.$intercom, 'Sent_Gift_Card')

        // The card is issued and recorded either way — only the guest's SMS failed
        // (D10). Say so instead of implying the card didn't happen, and do not block
        // the reply.
        if (result?.smsDelivered === false) {
          this.$notify({
            text: 'Gift card created, but the text to the guest failed to send. Contact them directly.',
            type: 'warn'
          })
        }
        return true
      } catch (error) {
        // Nothing here arms X-Confirm-Duplicate, and the request above never sends it.
        // A duplicate 409 means the backend found a committed card for this guest, and
        // the only outcome this composer can produce from there is another 409 — no
        // second card is funded by pressing Send again. Arming was defensible while the
        // toast read "press Send again to confirm"; without that line it would be a
        // confirmation nobody gave, and the risk is not symmetric — the operator who
        // made the first card still holds the rate-limit claim and would only earn a
        // 429, but a second operator on the same guest holds none and would fund a real
        // second card. The amount and the key stay staged, so a Send after the window
        // has passed is the operator's original card rather than a new one.
        //
        // Default duration, like every other notification in the app. A toast that
        // outstays the action it reports is read as a modal and sits over the next
        // send; the operator still has the timeline to check at their own pace.
        this.$notify({
          text: giftCardErrorMessage(error),
          type: 'error'
        })
        return false
      }
    },
    async verifyGiftCard() {
      const h = this.$createElement
      const titleVNode = h('div', [
        h('h2', { class: ['mb-0'], domProps: { innerHTML: 'Confirm Gift Card Creation' } })
      ])
      const messageVNode = h('div', { class: ['text-center'] }, [
        'A gift card for ',
        h('strong', '$' + this.giftCardAmount),
        ' will be created.'
      ])
      const confirmed = await this.$bvModal.msgBoxConfirm([messageVNode], {
        title: [titleVNode],
        size: 'md',
        okTitle: 'Create and Send',
        cancelTitle: 'Cancel',
        centered: true
      })
      return !!confirmed
    },
    resetAll() {
      this.message = ''
      this.$refs.promoDropdown.reset()
      this.$refs.templateDropdown?.reset()
      this.giftCardAmount = null
      this.giftCardIdempotencyKey = null
      this.refreshKey += 1
    },
    /**
     * Stages an amount and resolves the Idempotency-Key that goes with it.
     *
     * The key is looked up from local storage before a new one is minted, because
     * component state does not survive a reload — and a reload is exactly what an
     * operator does after the gateway abandons their request at 29s while the Lambda
     * runs on to 90s and funds the card. A fresh key there makes the retry a second
     * issuance rather than a replay.
     *
     * Scoped by guest AND amount: replaying a key against a different body answers
     * `409 idempotency-key-reuse` and issues nothing, so a changed amount must mint.
     */
    async setGiftCardAmount(value) {
      this.giftCardAmount = value

      if (!value) {
        this.giftCardIdempotencyKey = null
        return
      }

      const customerId = this.selectActiveConversation?.customer?._id
      const amountCents = Math.round(parseFloat(value) * 100)

      const staged = await this.getGiftCardIdempotencyKey({ customerId, amountCents })
      if (staged) {
        this.giftCardIdempotencyKey = staged
        return
      }

      if (!this.giftCardIdempotencyKey) {
        this.giftCardIdempotencyKey = generateIdempotencyKey()
      }
      await this.saveGiftCardIdempotencyKey({
        customerId,
        amountCents,
        key: this.giftCardIdempotencyKey
      })
    },
    addPromo(promo) {
      this.promoId = promo._id
    },
    openPromo() {
      this.$refs.promoDropdown.reset()
      this.$refs.promoDropdown.openPromoDropdown()
    },
    resetPromo() {
      this.promoId = undefined
    },
    async addTemplate(template) {
      if (!template.promo && template.promptEachTime) {
        this.confirmAddPromo()
      } else if (template.promo) {
        this.$refs.promoDropdown.openPromoDropdown(template.promo)
      }
      const response = await TemplateService.interpolateTemplate({
        templateId: template._id,
        locationId: this.selectActiveConversationLocation?._id,
        customerId: this.selectActiveConversationCustomer?._id
      })
      this.message = response.body.data.interpolatedTemplate
    },
    resetTemplate() {
      this.message = ''
    },
    confirmAddPromo() {
      this.$bvModal
        .msgBoxConfirm('Would you like to add an offer to this response?', {
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'primary',
          cancelVariant: 'default',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        })
        .then((value) => {
          if (value) {
            this.$refs.promoDropdown.openPromoDropdown()
          }
        })
        .catch((err) => {
          this.$notify({
            type: 'error',
            text: 'Something went wrong, please try again.'
          })
        })
    },
    async generateResponse() {
      this.isGenerating = true
      try {
        const { prompt, reply, usage, validated } = await this.generateReply({
          conversationId: this.selectActiveConversation._id
        })
        if (!validated) {
          this.$bvModal.msgBoxConfirm(
            'Heads up! Our AI can sometimes have trouble with names. Please ensure that any names in the response are accurate before sending.',
            {
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'primary',
              cancelVariant: 'default',
              okTitle: 'Ok',
              cancelTitle: 'Cancel',
              footerClass: 'p-2',
              hideHeaderClose: false,
              centered: true
            }
          )
        }
        this.message = reply
        trackEvent(this.$intercom, 'AI quick response', {
          reply,
          usage,
          prompt,
          companyId: this.activeCompany._id,
          conversationId: this.selectActiveConversation._id
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Error generating AI response'
        })
      }
      this.isGenerating = false
    },
    isMentionedTextFormat(text) {
      const mentionTagRegex = /<span\s+class=["']mention["'][^>]*data-value=["'][^"']+["'][^>]*>/g
      return mentionTagRegex.test(text)
    }
  },
  watch: {
    selectLastEvent: {
      immediate: true,
      handler() {
        if (
          (this.selectLastActionableEvent?.type === 'internal-note-added' ||
            this.lastCommunicationType === 'note') &&
          !this.message &&
          this.selectIsPermitted('noteWriteAccess')
        ) {
          if (!this.isNote) {
            this.setIsNote(true)
          }
          const user = this.selectLastActionableEvent?.user
          if (user && user._id !== this.onlineUser._id) {
            this.message = `<p><span class="mention" data-index="0" data-denotation-char="@" data-value="${
              user.firstName + ' ' + user.lastName
            }"><span contenteditable="false"><span class="ql-mention-denotation-char">@</span>${
              user.firstName + ' ' + user.lastName
            }</span></span> </p>`
          }
        }
      }
    },
    selectActiveConversation: {
      immediate: true,
      async handler(val, oldVal) {
        if (!val?._id) return

        const hasIdChanged = val._id !== oldVal?._id
        const hasWriteAccess = this.selectIsPermitted('winbackWriteAccess')

        if (hasIdChanged && hasWriteAccess) {
          this.setIsNote(false)
        }

        // A staged amount is scoped to the conversation it was set on — carrying it
        // over would fund a card for the wrong guest and location. refreshKey must
        // bump too, or GiftCardButton's own local amount survives the switch.
        // The staged key itself is NOT cleared from storage here — surviving a switch
        // (and a reload) is the point. Only the in-memory staging resets, and
        // setGiftCardAmount restores the key if the same guest is staged again.
        if (hasIdChanged) {
          this.giftCardAmount = null
          this.giftCardIdempotencyKey = null
          this.refreshKey += 1
        }

        const cachedMessage = await this.getCachedMessage(val._id)

        if (cachedMessage) {
          const isMentions = this.isMentionedTextFormat(cachedMessage)
          if (isMentions && !this.isNote && hasWriteAccess) {
            this.setIsNote(true)
          }
          this.message = cachedMessage
        }
      }
    },
    message: debounce(async function (message) {
      const conversationId = this.selectActiveConversation?._id
      if (!conversationId) return

      if (message) {
        await this.setMessageCache({ message, conversationId })
      } else {
        await this.removeMessageCache(conversationId)
      }
    }, 500)
  }
}
</script>

<style lang="scss" scoped>
.action-icon {
  margin-right: 12px;
  color: #95aac9;
}

.action-icon:hover {
  color: #12263f !important;
}

::v-deep #aiButton > svg {
  fill: #95aac9 !important;
}

::v-deep #aiButton > svg:hover {
  fill: #12263f !important;
}

::v-deep #aiButton > svg > path {
  fill: inherit !important;
}

.message-builder {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}

.message-builder-card {
  position: relative;
}

.inactive-send {
  color: #95aac9 !important;
  font-weight: 400;
}

.active-send {
  color: #4184f4;
  font-weight: 700;
}

.active-add {
  color: #12263f;
  font-weight: 700;
}

.send-btn {
  width: 80px;
}

.note-card {
  background: #fdf3d8 !important;
  border-color: #fbe6b1 !important;
}

::v-deep .avatar {
  vertical-align: middle;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  margin-right: 3px;
}

::v-deep .initials-avatar {
  width: 25px;
  height: 25px;
  border-radius: 50%;
  font-size: 10px;
  color: white;
  line-height: 25px;
  text-align: center;
  background: #b1c2d9;
  margin-right: 3px;
  vertical-align: middle;
}

::v-deep .ql-mention-list-container {
  overflow: scroll;
  max-height: 185px;
  width: auto;
  min-width: 270px;
  padding: 3px 0px 3px 0px;
}

::v-deep .ql-mention-list-item.selected {
  background-color: #edf2f9;
}

::v-deep .ql-mention-list-item {
  line-height: 38px;
}

::v-deep .ql-container.ql-snow {
  border: none;
}

::v-deep .ql-editor {
  border: none;
  background: #fdf3d8;
  min-height: 40px;
  padding: 8px 12px;
  font-size: 0.9375rem;
  font-family: 'Poppins', sans-serif;
}

::v-deep .ql-editor.ql-blank::before {
  color: #95aac9;
  font-style: normal;
}

::v-deep .mention {
  background-color: #fbe6b1;
}
</style>
